/* eslint-disable @typescript-eslint/no-explicit-any */
interface Keymap {
  [key: string]: string
}

const accentsMap: Keymap = {
  a: 'á|à|ã|â|À|Á|Ã|Â',
  e: 'é|è|ê|É|È|Ê',
  i: 'í|ì|î|Í|Ì|Î',
  o: 'ó|ò|ô|õ|Ó|Ò|Ô|Õ',
  u: 'ú|ù|û|ü|Ú|Ù|Û|Ü',
}

export function removeDiacritics(text: string) {
  return Object.keys(accentsMap).reduce(
    (acc, cur) => acc.replace(new RegExp(accentsMap[cur], 'g'), cur),
    text,
  )
}

export function throttle(func: any, timeFrame: number) {
  let lastTime = 0
  return function _throttle() {
    const now: number = (new Date() as unknown) as number
    if (now - lastTime >= timeFrame) {
      func()
      lastTime = now
    }
  }
}

export function escapeRegexCharacters(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function matchHighlightString(text = '', query = '') {
  const cleanInput = removeDiacritics(text)
  const cleanQuery = removeDiacritics(query)
  return (
    cleanQuery
      .trim()
      .split(/\s+/)
      // If query is blank, we'll get empty string here, so let's filter it out.
      .filter((word) => word.length > 0)
      .reduce((result: any[], word) => {
        const wordLen = word.length
        const prefix = /[a-z0-9_]/i.test(word[0]) ? '\\b' : ''
        const regex = new RegExp(prefix + escapeRegexCharacters(word), 'i')
        const index = cleanInput.search(regex)

        if (index > -1) {
          return [...result, [index, index + wordLen]]
        }

        return [...result]
      }, [])
      .sort(function sort(match1, match2) {
        return match1[0] - match2[0]
      })
  )
}

export function parseHighlightString(text: string, matches: Array<number>[] = []) {
  const result = []

  if (matches.length === 0) {
    result.push({
      text,
      highlight: false,
    })
  } else if (matches[0][0] > 0) {
    result.push({
      text: text.slice(0, matches[0][0]),
      highlight: false,
    })
  }

  matches.forEach(function add(match, i) {
    const startIndex = match[0]
    const endIndex = match[1]

    result.push({
      text: text.slice(startIndex, endIndex),
      highlight: true,
    })

    if (i === matches.length - 1) {
      if (endIndex < text.length) {
        result.push({
          text: text.slice(endIndex, text.length),
          highlight: false,
        })
      }
    } else if (endIndex < matches[i + 1][0]) {
      result.push({
        text: text.slice(endIndex, matches[i + 1][0]),
        highlight: false,
      })
    }
  })

  return result
}

export const findByValue = (collection: any, valueToFind: any) => {
  const value = collection?.find(
    (element: any) => element?.value.toUpperCase() === valueToFind?.toUpperCase(),
  )
  return value?.id ? { id: value?.id } : ''
}

export const findById = (collection: any, idToFind: any) => {
  const element = collection?.find((element: any) => element?.id === idToFind)
  return element?.value ? element.value : ''
}

export const filterFalsyValues = (obj: any) =>
  /* eslint-disable-next-line */
  Object.entries(obj).reduce(
    /* eslint-disable-next-line */
    (a, [k, v]) => (v ? (((a as { [key: string]: any })[k] = v), a) : a),
    {},
  )

export const capitalize = (str: string | undefined) => {
  if (typeof str !== 'string') return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const formatToIsoDate = (date: string) => {
  const splitDate = date.split('/')
  return new Date(
    parseInt(splitDate[2], 10),
    parseInt(splitDate[1], 10) - 1,
    parseInt(splitDate[0], 10),
  )
}

export const compareArrayValues = (arr1: [], arr2: []) => {
  const arr1test = arr1.slice().sort()
  const arr2test = arr2.slice().sort()
  return !arr1test.some((val, i) => val !== arr2test[i])
}
